import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

export const fetchExams = createAsyncThunk(
  "exams/fetchExams",
  async (examCategoryId = null, { rejectWithValue }) => {
    try {
      const examMasterService = (await import("../../services/examMasterService")).default;
      return await examMasterService.getExams(examCategoryId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch exams");
    }
  }
);

export const fetchActiveExams = createAsyncThunk(
  "exams/fetchActiveExams",
  async (_, { rejectWithValue }) => {
    try {
      const examMasterService = (await import("../../services/examMasterService")).default;
      return await examMasterService.getActiveExams();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch active exams");
    }
  }
);

export const createExam = createAsyncThunk(
  "exams/createExam",
  async (payload, { rejectWithValue }) => {
    try {
      const examMasterService = (await import("../../services/examMasterService")).default;
      const data = payload?.data || payload;
      const imageFile = payload?.imageFile || null;
      return await examMasterService.createExam(data, imageFile);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create exam");
    }
  }
);

export const updateExam = createAsyncThunk(
  "exams/updateExam",
  async ({ id, data, imageFile }, { rejectWithValue }) => {
    try {
      const examMasterService = (await import("../../services/examMasterService")).default;
      return await examMasterService.updateExam(id, data, imageFile);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update exam");
    }
  }
);

export const deleteExam = createAsyncThunk(
  "exams/deleteExam",
  async (id, { rejectWithValue }) => {
    try {
      const examMasterService = (await import("../../services/examMasterService")).default;
      await examMasterService.deleteExam(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete exam");
    }
  }
);

const examSlice = createSlice({
  name: "exams",
  initialState: {
    exams: [],
    activeExams: [],
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchExams.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchExams.fulfilled, (state, action) => {
        state.isLoading = false;
        state.exams = action.payload;
      })
      .addCase(fetchExams.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Active
      .addCase(fetchActiveExams.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchActiveExams.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activeExams = action.payload;
      })
      .addCase(fetchActiveExams.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createExam.fulfilled, (state, action) => {
        state.exams.unshift(action.payload);
        state.success = "Exam created successfully";
      })
      // Update
      .addCase(updateExam.fulfilled, (state, action) => {
        const index = state.exams.findIndex(e => e.id === action.payload.id);
        if (index !== -1) {
          state.exams[index] = action.payload;
        }
        state.success = "Exam updated successfully";
      })
      // Delete
      .addCase(deleteExam.fulfilled, (state, action) => {
        state.exams = state.exams.filter(e => e.id !== action.payload);
        state.success = "Exam deleted successfully";
      });
  },
});

export const { clearError, clearSuccess } = examSlice.actions;
export default examSlice.reducer;
